// components/inputs/PiInput.js
import React, { useState, useEffect, useRef } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";
import { normalizePreviewLabel } from "./previewLabel";
import { numericMathExpression, previewMathExpression } from "./mathExpression";
/* ====== Stałe layoutu (jak w FractionInput) ====== */
const INDENT = 36; // px – kolumna na ikonkę + odstęp
const PANEL_WIDTH_DESKTOP = 250; // px – szerokość panelu (bez INDENT)

export function parsePiLikeInput(raw) { return numericMathExpression(raw); }

export function formatPiValueLatex(input) { return previewMathExpression(input); }

export function formatPiLatexPreview(input, label = "x") {
  label = normalizePreviewLabel(label);
  const val = formatPiValueLatex(input);
  return val ? `${label} = ${val}` : null;
}

/* =========================
   NOWE: parse "a ± k*b" w jednym polu
   - a oraz b parsujemy parsePiLikeInput (czyli mogą mieć π albo nie)
   - przykłady:
     "pi/2 + k*pi/6"
     "π/2-kπ/6"
     "kπ/6"
     "-k*pi/3"
     "pi/6 + kpi/3"
   ========================= */
export function parsePiAffineInput(raw) {
  const s0 = String(raw ?? "")
    .trim()
    .replace(/[−–—]/g, "-")
    .replace(/\s+/g, "")
    .replace(/\\pi/g, "pi")
    .replace(/π/g, "pi");

  if (!s0) return null;

  const kIdx = s0.toLowerCase().indexOf("k");
  if (kIdx < 0) return null;

  // jeśli jest tylko "k" lub "k*" itd.
  // wyznacz operator (+/-) bezpośrednio przed 'k'
  const beforeK = s0.slice(0, kIdx);
  const afterK = s0.slice(kIdx + 1); // po "k"

  let op = "+";
  let aPart = beforeK;
  let coefBeforeK = "";

  if (beforeK.endsWith("+")) {
    op = "+";
    aPart = beforeK.slice(0, -1);
  } else if (beforeK.endsWith("-")) {
    op = "-";
    aPart = beforeK.slice(0, -1);
  } else if (beforeK === "") {
    // zaczyna się od k...
    op = "+";
    aPart = "";
  } else {
    // Akceptujemy także naturalny zapis 3k*pi oraz a+3k*pi.
    let separator = -1;
    for (let index = 1; index < beforeK.length; index += 1) {
      if (beforeK[index] === "+" || beforeK[index] === "-") separator = index;
    }
    if (separator >= 0) {
      op = beforeK[separator];
      aPart = beforeK.slice(0, separator);
      coefBeforeK = beforeK.slice(separator + 1);
    } else if (/^[+-]?\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?$/.test(beforeK)) {
      op = beforeK.startsWith("-") ? "-" : "+";
      aPart = "";
      coefBeforeK = beforeK.replace(/^[+-]/, "");
    }
  }

  // bPart: usuń wiodące mnożenie
  let bPart = afterK;
  if (bPart.startsWith("*") || bPart.startsWith("·")) bPart = bPart.slice(1);

  // jeśli po k nic nie ma → niepoprawne
  if (!bPart) return null;

  if (coefBeforeK) bPart = `${coefBeforeK}*${bPart}`;

  // a może być puste → 0
  const aRaw = aPart || "0";
  const bRaw = (op === "-" ? "-" : "") + bPart;

  const parsedA = parsePiLikeInput(aRaw);
  const parsedB = parsePiLikeInput(bRaw);

  if (!Number.isFinite(parsedA) || !Number.isFinite(parsedB)) return null;

  return {
    aRaw,
    bRaw,
    parsedA,
    parsedB,
  };
}

export function formatAffineLatexPreview(input, label = "x", setStyle = true) {
  label = normalizePreviewLabel(label);
  const aff = parsePiAffineInput(input);
  if (!aff) return null;

  const aLatex = formatPiValueLatex(aff.aRaw) ?? "\\square";
  const bLatex = formatPiValueLatex(aff.bRaw) ?? "\\square";

  // "x ∈ { a + k·b : k ∈ Z }" – bo to rodzina rozwiązań
  if (setStyle) {
    return `${label}\\in\\left\\{${aLatex}+k\\cdot${bLatex}:\\;k\\in\\mathbb{Z}\\right\\}`;
  }
  return `${label}=${aLatex}+k\\cdot${bLatex}`;
}

/* ========== komponent z klawiaturą, jak FractionInput ========== */
export default function PiInput({
  value,
  onChange,
  disabled,
  label: inputLabel = "x",
  placeholder = "np. 10π, 15*pi, 31,40",
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
  showPreview = true,      // ✅ nowe (wstecznie ok)
  allowAffine = false,     // ✅ nowe: pozwala wpisać "a + k*b"
  affineSetPreview = false // ✅ jeśli true: podgląd w stylu x ∈ { ... }
}) {
  const label = normalizePreviewLabel(inputLabel);
  // Stabilizacja wartości
  const pickString = (v) =>
    v && typeof v === "object" && "raw" in v ? (v.raw ?? "") : (v ?? "");
  const [local, setLocal] = useState(pickString(value));
  const [kbdOpen, setKbdOpen] = useState(false);

  // refy
  const inputRef = useRef(null);

  // synchronizacja z zewnątrz
  useEffect(() => {
    setLocal(pickString(value));
  }, [value]);

  const updateLocal = (raw) => {
    setLocal(raw);
    const affine = allowAffine ? parsePiAffineInput(raw) : null;
    onChange?.(affine ? {raw, parsed: {a: affine.parsedA, b: affine.parsedB}, affine} : {raw, parsed: parsePiLikeInput(raw)});
  };

  // caret i wstawianie
  const setCaret = (pos) => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    updateLocal(next);
    setTimeout(() => setCaret(s + text.length), 0);
  };

  // dedykowany skrót do wstawienia znaku π
  const insertPi = () => insertAtCursor("π");

  // style (jak w FractionInput)
  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };

  const inputStyle = {
    width: `calc(100% - ${indentPx}px)`,
    minWidth: 0,
    flex: "0 0 auto",
  };

  // podgląd (latex)
  let previewLatex = null;
  const incompleteAffine = allowAffine && /k/i.test(local) && !parsePiAffineInput(local);
  if (allowAffine) {
    previewLatex = formatAffineLatexPreview(local, label, affineSetPreview);
  }
  if (!previewLatex && !incompleteAffine) {
    previewLatex = formatPiLatexPreview(local, label);
  }

  return (
    <div style={containerStyle}>
      {/* wiersz: [ikona klawiatury][input + przycisk π] */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
        />

        <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
          <input
            ref={inputRef}
            type="text"
            value={local}
            onChange={(e) => updateLocal(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="input-secondary"
            style={inputStyle}
          />
          <button
            type="button"
            onClick={insertPi}
            disabled={disabled}
            className="btn-secondary"
          >
            π
          </button>
        </div>
      </div>

      {/* panel klawiatury pod inputem */}
      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={(type, payload) => {
              if (type === "insert") insertAtCursor(String(payload ?? ""));
              if (type === "backspace") {
                const el = inputRef.current;
                if (!el) return;
                const s = el.selectionStart,
                  e = el.selectionEnd;
                if (s !== e) {
                  const next = local.slice(0, s) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s), 0);
                } else if (s > 0) {
                  const next = local.slice(0, s - 1) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s - 1), 0);
                }
                return;
              }
              if (type === "moveLeft") {
                const el = inputRef.current;
                if (!el) return;
                const s = el.selectionStart,
                  e = el.selectionEnd;
                setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0);
              }
              if (type === "moveRight") {
                const el = inputRef.current;
                if (!el) return;
                const s = el.selectionStart,
                  e = el.selectionEnd;
                setTimeout(
                  () => setCaret(Math.min(local.length, Math.max(s, e) + 1)),
                  0
                );
              }
              if (type === "clear") {
                updateLocal("");
                setTimeout(() => setCaret(0), 0);
              }
            }}
            compact
            panelIndent={INDENT}
          />
        </div>
      )}

      {/* podgląd LaTeX */}
      {showPreview && local && previewLatex && (
        <div style={{ marginTop: 6, marginBottom: 0, pointerEvents: "none" }}>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={`\\displaystyle ${previewLatex}`} />
        </div>
      )}
      {showPreview && local && incompleteAffine && (
        <div style={{ marginTop: 6, marginBottom: 0, opacity: 0.82, pointerEvents: "none" }}>
          Po <InlineMath math={"k"} /> wpisz okres, np. <InlineMath math={"k\\pi"} />.
        </div>
      )}
    </div>
  );
}
